import React, { useEffect, useMemo, useState } from "react";
import { Check, ChevronDown, ChevronUp, ExternalLink, Image as ImageIcon } from "lucide-react";

const compact = (value, max = 180) => {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  return text.length <= max ? text : `${text.slice(0, max).trim()}…`;
};

const EMPTY_PRODUCT_DETAILS = Object.freeze({
  material: "",
  intendedUse: "",
  construction: "",
  articleRole: "",
});

const normalizeProductDetails = (details) => ({
  material: compact(details?.material, 400),
  intendedUse: compact(details?.intendedUse ?? details?.intended_use, 400),
  construction: compact(details?.construction, 400),
  articleRole: ["complete_machine", "part", "accessory"].includes(details?.articleRole ?? details?.article_role)
    ? (details?.articleRole ?? details?.article_role)
    : "",
});

const SEARCH_FIELD_PREFIXES = Object.freeze({
  material: /^(?:(?:재질|성분|조성)(?:\s*[·/]\s*(?:구성|성분|조성))?)\s*[:：=\-]?\s*/u,
  intendedUse: /^(?:(?:용도|사용처)(?:\s*[·/]\s*(?:용도|사용처))?)\s*[:：=\-]?\s*/u,
  construction: /^(?:(?:형태|구조|작동\s*방식)(?:\s*[·/]\s*(?:형태|구조|작동\s*방식))*)\s*[:：=\-]?\s*/u,
});

const valueOnlySearchFact = (value, field) => {
  let text = compact(value, 90);
  const prefix = SEARCH_FIELD_PREFIXES[field];
  if (prefix) text = text.replace(prefix, "").trim();
  // "핸드백 형태", "회전 작동방식"처럼 필드명을 값의 끝에 반복해도
  // 검색에는 실질 속성만 전달한다. 원래 확인 사실(productDetails)은 보존한다.
  if (field === "construction") {
    text = text.replace(/\s+(?:형태|구조|작동\s*방식)\s*$/u, "").trim();
  }
  return text;
};

export function buildNaverExpandedQuery(query, candidate, productDetails = {}) {
  const base = compact(query, 160);
  const title = compact(candidate?.title, 100);
  const details = normalizeProductDetails(productDetails);
  // 필드명은 입력 UI의 라벨일 뿐 검색어가 아니다. "형태" 같은 일반어가
  // 관세율표 법정 문언을 광범위하게 맞추지 않도록 확인된 값만 이어 붙인다.
  const detailWords = [
    valueOnlySearchFact(details.material, "material"),
    valueOnlySearchFact(details.intendedUse, "intendedUse"),
    valueOnlySearchFact(details.construction, "construction"),
  ].filter(Boolean).join(" ");
  // AI/사용자 확인 정보가 있으면 짧은 검색 요약보다 확인된 필드를 우선한다.
  const description = !detailWords && candidate?.type === "web" ? compact(candidate?.description, 100) : "";
  const words = `${title} ${detailWords} ${description}`.replace(/\s+/g, " ").trim();
  if (!words || base.toLowerCase().includes(words.toLowerCase())) return base;
  return compact(`${base} ${words}`, 300);
}

function CandidateButton({ candidate, selected, onSelect }) {
  const image = candidate.type === "image";
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={() => onSelect(candidate)}
      style={{
        position: "relative",
        width: image ? 126 : 230,
        minWidth: image ? 126 : 230,
        minHeight: image ? 150 : 92,
        padding: image ? 6 : 10,
        border: selected ? "2px solid var(--audit-text)" : "1px solid var(--audit-border)",
        borderRadius: 7,
        background: selected ? "var(--audit-bg-elevated)" : "var(--audit-bg)",
        color: "var(--audit-text)",
        textAlign: "left",
        cursor: "pointer",
        fontFamily: "inherit",
        overflow: "hidden",
      }}
    >
      {selected && (
        <span style={{ position: "absolute", top: 6, right: 6, zIndex: 1, width: 20, height: 20, display: "grid", placeItems: "center", borderRadius: "50%", color: "white", background: "#202124" }}>
          <Check size={13} />
        </span>
      )}
      {image ? (
        <>
          <img
            src={candidate.thumbnail_url}
            alt={candidate.title || "웹 이미지 검색 후보"}
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={(event) => { event.currentTarget.style.visibility = "hidden"; }}
            style={{ display: "block", width: "100%", height: 104, objectFit: "cover", borderRadius: 4, background: "var(--audit-bg-elevated)" }}
          />
          <div style={{ marginTop: 6, fontSize: 12, lineHeight: 1.35, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {candidate.title || "이미지 후보"}
          </div>
        </>
      ) : (
        <>
          <div style={{ paddingRight: 18, fontSize: 13, fontWeight: 650, lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {candidate.title}
          </div>
          <div style={{ marginTop: 5, color: "var(--audit-text-muted)", fontSize: 12, lineHeight: 1.45, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {candidate.description}
          </div>
        </>
      )}
    </button>
  );
}

export default function NaverMeaningCandidates({ q, applied, onApply, initialProductDetails }) {
  const initialMaterial = initialProductDetails?.material || "";
  const initialIntendedUse = initialProductDetails?.intendedUse || "";
  const initialConstruction = initialProductDetails?.construction || "";
  const initialArticleRole = initialProductDetails?.articleRole || "";
  const normalizedInitialProductDetails = useMemo(
    () => normalizeProductDetails({
      material: initialMaterial,
      intendedUse: initialIntendedUse,
      construction: initialConstruction,
      articleRole: initialArticleRole,
    }),
    [initialMaterial, initialIntendedUse, initialConstruction, initialArticleRole],
  );
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState({ web: [], images: [] });
  const [selected, setSelected] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [profileDraft, setProfileDraft] = useState(() => normalizedInitialProductDetails);
  const [profileMeta, setProfileMeta] = useState({ model: "", evidenceSummary: "", warnings: [] });

  useEffect(() => {
    if (!q || String(q).trim().length < 2 || applied) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setPayload({ web: [], images: [] });
    setSelected(null);
    setProfileOpen(false);
    setProfileError("");
    setProfileDraft(normalizedInitialProductDetails);
    fetch("/api/onto/naver-meaning-candidates", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ q }),
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => setPayload({
        web: Array.isArray(data.web) ? data.web.map((item, index) => ({ ...item, type: "web", id: `web-${index}` })) : [],
        images: Array.isArray(data.images) ? data.images.map((item, index) => ({ ...item, type: "image", id: `image-${index}` })) : [],
      }))
      .catch((error) => {
        if (error?.name !== "AbortError") setPayload({ web: [], images: [] });
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [q, applied, normalizedInitialProductDetails]);

  const count = payload.web.length + payload.images.length;
  const selectedCandidate = useMemo(
    () => [...payload.web, ...payload.images].find((item) => item.id === selected),
    [payload, selected],
  );

  const selectCandidate = (candidate) => {
    setSelected(candidate.id);
    setProfileOpen(false);
    setProfileError("");
    setProfileMeta({ model: "", evidenceSummary: "", warnings: [] });
    setProfileDraft(normalizedInitialProductDetails);
  };

  const requestProfileSuggestion = async () => {
    if (!selectedCandidate || profileLoading) return;
    setProfileOpen(true);
    setProfileLoading(true);
    setProfileError("");
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 65_000);
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token") || "";
      const response = await fetch("/api/onto/product-profile-suggestion", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          original_query: q,
          candidate: {
            type: selectedCandidate.type,
            title: selectedCandidate.title || "",
            description: selectedCandidate.type === "web" ? selectedCandidate.description || "" : "",
            url: selectedCandidate.url || selectedCandidate.image_url || "",
          },
        }),
        signal: controller.signal,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data?.success === false) {
        throw new Error(data?.error || `HTTP ${response.status}`);
      }
      const suggested = normalizeProductDetails(data?.proposal);
      const existing = normalizedInitialProductDetails;
      setProfileDraft({
        material: existing.material || suggested.material,
        intendedUse: existing.intendedUse || suggested.intendedUse,
        construction: existing.construction || suggested.construction,
        articleRole: existing.articleRole || suggested.articleRole,
      });
      setProfileMeta({
        model: data?.model || "",
        evidenceSummary: compact(data?.evidence_summary, 500),
        warnings: Array.isArray(data?.warnings) ? data.warnings.slice(0, 5).map((item) => compact(item, 240)).filter(Boolean) : [],
      });
    } catch (error) {
      setProfileDraft(normalizedInitialProductDetails);
      setProfileMeta({ model: "", evidenceSummary: "", warnings: [] });
      setProfileError(error?.name === "AbortError"
        ? "상품정보 자동 정리 시간이 초과되었습니다. 직접 입력해 계속할 수 있습니다."
        : (error?.message || "상품정보를 자동으로 정리하지 못했습니다. 직접 입력해 계속할 수 있습니다."));
    } finally {
      window.clearTimeout(timeout);
      setProfileLoading(false);
    }
  };

  const updateProfile = (field, value) => setProfileDraft((current) => ({ ...current, [field]: value }));
  const confirmAndApply = (withoutDetails = false) => {
    if (!selectedCandidate) return;
    const confirmedDetails = withoutDetails ? { ...EMPTY_PRODUCT_DETAILS } : normalizeProductDetails(profileDraft);
    onApply(selectedCandidate, confirmedDetails, profileMeta);
  };

  if (applied) {
    return (
      <div style={{ marginBottom: 12, padding: "9px 11px", border: "1px solid var(--audit-border)", borderRadius: 7, background: "var(--audit-bg-elevated)", fontSize: 12.5 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
          <Check size={14} aria-hidden="true" />
          <b>웹 검색 의미 후보를 사용자가 선택했습니다.</b>
          <span style={{ color: "var(--audit-text-muted)" }}>{applied.title}</span>
        </div>
        <div style={{ marginTop: 5, fontSize: 11.5, lineHeight: 1.5, color: "var(--audit-text-muted)" }}>
          이미지·제목은 상품 동일성의 단서로만 사용했습니다. 재질·성분·용도는 아래 <b style={{ color: "var(--audit-text)" }}>AI 웹검증</b>에서 제조사·공식 자료로 확인하며, 사용자 확인 전에는 결정트리 사실이 아닙니다.
        </div>
      </div>
    );
  }

  if (!loading && count === 0) return null;

  return (
    <section style={{ marginBottom: 12, border: "1px solid var(--audit-border)", borderRadius: 7, background: "var(--audit-bg)" }}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        style={{ width: "100%", padding: "8px 11px", border: 0, borderRadius: 7, background: "var(--audit-bg-elevated)", color: "var(--audit-text)", display: "flex", alignItems: "center", gap: 7, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}
      >
        <ImageIcon size={15} />
        <b style={{ fontSize: 13 }}>웹 검색으로 의미 넓히기</b>
        <span style={{ fontSize: 11.5, color: "var(--audit-text-muted)" }}>
          {loading ? "후보를 찾는 중…" : `웹·이미지 ${count}건 · 사용자 선택 필요`}
        </span>
        <span style={{ marginLeft: "auto" }}>{open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}</span>
      </button>

      {open && (
        <div style={{ padding: 10 }}>
          <div style={{ marginBottom: 8, fontSize: 11.5, color: "var(--audit-text-muted)" }}>
            제품과 같은 후보를 고르세요. 선택 전에는 분류 결과나 결정트리에 반영되지 않습니다.
          </div>
          {loading ? (
            <div style={{ padding: "10px 0", fontSize: 12, color: "var(--audit-text-muted)" }}>웹문서와 이미지를 확인하고 있습니다…</div>
          ) : (
            <>
              {payload.web.length > 0 && (
                <div style={{ display: "flex", gap: 7, overflowX: "auto", paddingBottom: 8 }}>
                  {payload.web.map((candidate) => <CandidateButton key={candidate.id} candidate={candidate} selected={selected === candidate.id} onSelect={selectCandidate} />)}
                </div>
              )}
              {payload.images.length > 0 && (
                <div style={{ display: "flex", gap: 7, overflowX: "auto", padding: "2px 0 8px" }}>
                  {payload.images.map((candidate) => <CandidateButton key={candidate.id} candidate={candidate} selected={selected === candidate.id} onSelect={selectCandidate} />)}
                </div>
              )}
              <div style={{ borderTop: "1px solid var(--audit-border)", paddingTop: 9 }}>
                <span style={{ display: "block", minWidth: 0, marginBottom: 7, fontSize: 12, color: selectedCandidate ? "var(--audit-text)" : "var(--audit-text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {selectedCandidate ? `선택: ${selectedCandidate.title}` : "제품과 같은 후보를 하나 선택하세요."}
                </span>
                <div aria-label="선택한 의미 적용" style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 9, flexWrap: "wrap" }}>
                  <button type="button" disabled={!selectedCandidate || profileLoading} onClick={requestProfileSuggestion} style={{ padding: "7px 11px", border: "1px solid #202124", borderRadius: 5, background: selectedCandidate ? "#202124" : "var(--audit-bg-elevated)", color: selectedCandidate ? "white" : "var(--audit-text-muted)", cursor: selectedCandidate && !profileLoading ? "pointer" : "default", fontFamily: "inherit", fontSize: 12.5, fontWeight: 700 }}>
                    {profileLoading ? "상품 정보 정리 중…" : "선택한 후보로 다시 검색"}
                  </button>
                  {selectedCandidate && (selectedCandidate.url || selectedCandidate.image_url) && (
                    <a href={selectedCandidate.url || selectedCandidate.image_url} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 3, color: "var(--audit-text-muted)", fontSize: 11.5, textDecoration: "none" }}>
                      원본 확인 <ExternalLink size={12} />
                    </a>
                  )}
                  <span style={{ fontSize: 11.5, color: "var(--audit-text-muted)" }}>
                    AI가 상품정보 초안을 채우고, 수정·확정한 내용만 재검색에 사용합니다.
                  </span>
                </div>
                {profileOpen && (
                  <div aria-label="상품 정보 자동 정리" style={{ marginTop: 10, padding: 12, border: "1px solid var(--audit-border-strong, #cdd1d8)", borderRadius: 7, background: "var(--audit-bg-elevated)" }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                      <b style={{ fontSize: 13 }}>상품 정보 자동 정리</b>
                      <span style={{ fontSize: 11.5, color: "var(--audit-text-muted)" }}>AI 제안은 초안입니다. 확인·수정한 뒤에만 검색과 결정트리에 반영됩니다.</span>
                    </div>
                    {profileError && <div role="alert" style={{ marginTop: 8, padding: "7px 9px", border: "1px solid var(--audit-border)", borderRadius: 5, fontSize: 11.5, color: "var(--audit-text-muted)", background: "var(--audit-bg)" }}>{profileError}</div>}
                    {profileMeta.evidenceSummary && <div style={{ marginTop: 8, fontSize: 11.5, lineHeight: 1.5, color: "var(--audit-text-muted)" }}>확인 근거: {profileMeta.evidenceSummary}</div>}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 9, marginTop: 10 }}>
                      <label style={{ minWidth: 0, fontSize: 12, fontWeight: 650 }}>
                        재질·구성
                        <input value={profileDraft.material} onChange={(event) => updateProfile("material", event.target.value)} placeholder="예: 셀룰로오스 70%, 면 30%" style={{ width: "100%", height: 34, boxSizing: "border-box", marginTop: 5, padding: "6px 9px", border: "1px solid var(--audit-border-strong, #cdd1d8)", borderRadius: 6, background: "var(--audit-bg)", color: "var(--audit-text)", fontFamily: "inherit", fontSize: 12.5 }} />
                      </label>
                      <label style={{ minWidth: 0, fontSize: 12, fontWeight: 650 }}>
                        용도·사용처
                        <input value={profileDraft.intendedUse} onChange={(event) => updateProfile("intendedUse", event.target.value)} placeholder="예: 주방용 물기 흡수·표면 닦기" style={{ width: "100%", height: 34, boxSizing: "border-box", marginTop: 5, padding: "6px 9px", border: "1px solid var(--audit-border-strong, #cdd1d8)", borderRadius: 6, background: "var(--audit-bg)", color: "var(--audit-text)", fontFamily: "inherit", fontSize: 12.5 }} />
                      </label>
                      <label style={{ minWidth: 0, fontSize: 12, fontWeight: 650 }}>
                        형태·작동방식
                        <input value={profileDraft.construction} onChange={(event) => updateProfile("construction", event.target.value)} placeholder="예: 다공성 셀룰러 시트, 직사각형" style={{ width: "100%", height: 34, boxSizing: "border-box", marginTop: 5, padding: "6px 9px", border: "1px solid var(--audit-border-strong, #cdd1d8)", borderRadius: 6, background: "var(--audit-bg)", color: "var(--audit-text)", fontFamily: "inherit", fontSize: 12.5 }} />
                      </label>
                      <label style={{ minWidth: 0, fontSize: 12, fontWeight: 650 }}>
                        품목 상태
                        <select value={profileDraft.articleRole} onChange={(event) => updateProfile("articleRole", event.target.value)} style={{ width: "100%", height: 34, boxSizing: "border-box", marginTop: 5, padding: "6px 9px", border: "1px solid var(--audit-border-strong, #cdd1d8)", borderRadius: 6, background: "var(--audit-bg)", color: "var(--audit-text)", fontFamily: "inherit", fontSize: 12.5 }}>
                          <option value="">모름 / 선택 안 함</option>
                          <option value="complete_machine">완성 기계·기기</option>
                          <option value="part">부분품</option>
                          <option value="accessory">부속품</option>
                        </select>
                      </label>
                    </div>
                    {profileMeta.warnings.length > 0 && <div style={{ marginTop: 8, fontSize: 11.5, lineHeight: 1.5, color: "var(--audit-text-muted)" }}>{profileMeta.warnings.join(" · ")}</div>}
                    <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginTop: 11 }}>
                      <button type="button" onClick={() => confirmAndApply(false)} style={{ padding: "7px 11px", border: "1px solid #202124", borderRadius: 5, background: "#202124", color: "white", fontFamily: "inherit", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>이 정보로 다시 검색</button>
                      <button type="button" onClick={() => confirmAndApply(true)} style={{ padding: "7px 11px", border: "1px solid var(--audit-border-strong, #cdd1d8)", borderRadius: 5, background: "var(--audit-bg)", color: "var(--audit-text)", fontFamily: "inherit", fontSize: 12, cursor: "pointer" }}>상품 정보 없이 계속</button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}
